import { Badge } from '@/components/ui/badge';
import { JsonViewer } from '@/components/ui/json-viewer';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

import { formatDate } from '@/lib/date';
import { normalizePayloadData } from '@/lib/utils';

import {
  formatMessageValue,
  getEventBadgeProps,
  getMessageEntries,
  getPayload,
  humanizeLabel,
} from '../audit-log.utils';

import type { AuditLog } from '@/types/audit-log.type';

type Props = {
  log: AuditLog | null;
  onClose: () => void;
};

const DetailField = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="space-y-1 rounded-lg border border-border/60 bg-muted/30 p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="break-words text-sm text-foreground">{value}</p>
    </div>
  );
};

const MessageField = ({ label, value }: { label: string; value: unknown }) => {
  const isStructuredValue = value != null && typeof value === 'object';

  return (
    <div className="space-y-2 rounded-lg border border-border/60 bg-muted/20 p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {humanizeLabel(label)}
      </p>
      {isStructuredValue ? (
        <div className="rounded-md border border-border/60 bg-background p-2">
          <JsonViewer data={value} rootName={label} defaultExpanded={true} />
        </div>
      ) : (
        <p className="break-words text-sm text-foreground">{formatMessageValue(value)}</p>
      )}
    </div>
  );
};

export function AuditLogDetailsSheet({ log, onClose }: Props) {
  const payload = log ? getPayload(log) : null;
  const messageEntries = log ? getMessageEntries(log) : [];
  const payloadData = normalizePayloadData(payload);

  return (
    <Sheet open={!!log} onOpenChange={open => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl h-full min-h-0 flex flex-col">
        {log ? (
          <>
            <SheetHeader className="border-b pb-4 pr-10">
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-2">
                  <SheetTitle className="text-left text-base font-semibold text-foreground">
                    Audit log #{log.id}
                  </SheetTitle>
                  <SheetDescription className="text-left">
                    Review the complete audit event payload and metadata for this log entry.
                  </SheetDescription>
                </div>
                <Badge
                  variant={getEventBadgeProps(log.type).variant}
                  className={getEventBadgeProps(log.type).className}
                >
                  {humanizeLabel(log.type)}
                </Badge>
              </div>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              <div className="grid gap-3 sm:grid-cols-2">
                <DetailField label="Module" value={log.module} />
                <DetailField label="User" value={log.user_email || '-'} />
                <DetailField label="Created at" value={formatDate(log.created_at)} />
                <DetailField label="Event type" value={humanizeLabel(log.type)} />
              </div>

              <div className="space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Message details</h3>
                  <p className="text-sm text-muted-foreground">
                    Activity fields captured for this event, excluding the structured payload.
                  </p>
                </div>
                {messageEntries.length === 0 ? (
                  <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
                    <p className="text-sm text-muted-foreground">
                      No additional message fields recorded for this event.
                    </p>
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {messageEntries.map(([key, value]) => (
                      <MessageField key={`${log.id}-${key}`} label={key} value={value} />
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Payload</h3>
                  <p className="text-sm text-muted-foreground">
                    Structured payload captured for this audit event.
                  </p>
                </div>
                <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
                  {payload == null ? (
                    <p className="text-sm text-muted-foreground">
                      No payload recorded for this event.
                    </p>
                  ) : (
                    <JsonViewer data={payloadData} rootName="payload" defaultExpanded={true} />
                  )}
                </div>
              </div>
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
